/**
 * LessonEditPreview Component
 *
 * Displays a published lesson draft (sequential clip player) with coach notes.
 * Handles loading the draft from Firestore and pre-fetching storage URLs so that
 * LessonPreview receives pre-loaded Mode 1 props and makes no Firebase calls itself.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { LessonPreview } from './LessonPreview';
import { getDraft, getStorageUrl } from '../services/videoEditDraft';
import { getLocalPlaybackProgress, saveLocalPlaybackProgress, getPlaybackProgress, savePlaybackProgress, clearPlaybackProgress } from '../services/lessonPlaybackProgress';
import { useAuth } from '../contexts/AuthContext';
import type { VideoContext, VoiceoverNotableSegmentClip, NotableSegment } from '../types/videoEdit';

export interface LessonEditPreviewProps {
  /** Video storage context (lesson context with lessonId) */
  context: VideoContext;
  /** ID of the original player video */
  originalVideoId: string;
  /** ID of the published draft to display */
  draftId: string;
  /** Coach's notes/commentary for the lesson */
  notes?: string;
  /** Name of the coach who created the edit */
  coachName?: string;
  /** Offering label for the fullscreen title, formatted by the caller. */
  offeringLabel?: string;
  /** ISO date the lesson was delivered, for the fullscreen title. */
  deliveredAt?: string;
  /** Auto-play video on load */
  autoPlay?: boolean;
  /** Show video player controls */
  showControls?: boolean;
  /** Callback when video playback ends */
  onEnded?: () => void;
  /** Fires whenever playback starts or stops — passed through to LessonPreview. */
  onPlayingChange?: (playing: boolean) => void;
  /** @deprecated Not used — errors are shown inline */
  onError?: (error: Error) => void;
  /** Optional className for the container */
  className?: string;
  /** URL or storage path to processed video for download */
  downloadUrl?: string;
  /** Firebase Storage path for the composited video — preferred for download to avoid CORS. */
  downloadStoragePath?: string;
}

const SAVE_INTERVAL_MS = 5_000;

export function LessonEditPreview({
  context,
  originalVideoId,
  draftId,
  notes,
  coachName,
  offeringLabel,
  deliveredAt,
  autoPlay = false,
  showControls = true,
  onEnded,
  onPlayingChange,
  className = '',
  downloadUrl,
  downloadStoragePath,
}: LessonEditPreviewProps) {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid ?? null;
  // context.id is the lessonId when context.type === 'lesson'
  const lessonId = context.id ?? '';
  const isLessonContext = context.type === 'lesson' && !!lessonId && !!userId;

  const [clips, setClips] = useState<VoiceoverNotableSegmentClip[]>([]);
  const [segments, setSegments] = useState<NotableSegment[]>([]);
  const [clipUrls, setClipUrls] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Real fullscreen where the API exists; the CSS overlay where it does not.
   *
   * Both paths drive the same `isFullscreen` flag, so nothing downstream has to
   * know which one is in play — the wrapper's className, LessonPreview's icon
   * and its label colours all read one boolean.
   *
   * Capability, not user agent: iPhone Safari simply has no
   * Element.requestFullscreen, so the check is for the method itself plus
   * document.fullscreenEnabled (which is false inside an iframe that has not
   * been granted the permission). iPad reports the method and takes the real
   * path; iPhone falls through to the overlay.
   *
   * `webkitEnterFullscreen` is deliberately not used on iPhone: it hands the
   * <video> to Apple's player, whose scrubber shows the current clip's duration
   * rather than the lesson's, so a seven-clip lesson would present as a
   * forty-second video.
   */
  const handleFullscreenToggle = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const canNative =
      typeof el.requestFullscreen === 'function' && document.fullscreenEnabled;

    if (!canNative) {
      setIsFullscreen((v) => !v);
      return;
    }

    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
      return;
    }

    /* flushSync, and this is the load-bearing line. The wrapper is
       `display: contents` when not expanded, so it generates no box and there
       is nothing for the browser to fullscreen. Flushing the state first gives
       it the overlay's box synchronously, and because flushSync runs inside
       this click handler the user activation that requestFullscreen requires is
       still live. Requesting from an effect keyed on the state change instead
       is the shape that rejects with NotAllowedError — the most likely reason
       the original attempts failed. */
    flushSync(() => setIsFullscreen(true));
    el.requestFullscreen().catch(() => {
      /* Rejected for any reason: keep the overlay rather than leaving the
         control looking broken. */
    });
  }, []);

  /* document.fullscreenElement is the source of truth once the real path is in
     use, so Escape, the browser's own exit button and F11 all land here without
     special casing. Ignored while the overlay path is running, where no
     fullscreenchange event ever fires. */
  useEffect(() => {
    const onChange = () => {
      if (!containerRef.current) return;
      if (document.fullscreenElement === containerRef.current) setIsFullscreen(true);
      else if (document.fullscreenElement === null) setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ── Playback progress persistence ─────────────────────────────────────────

  // Saved position loaded from Firestore on mount; passed to LessonPreview as
  // initialVirtualTime so it can seek there once clip durations are resolved.
  const [savedVirtualTime, setSavedVirtualTime] = useState(0);

  // Refs track the latest position so the unmount cleanup can save it without
  // needing the values as effect dependencies.
  const latestVirtualTimeRef = useRef(0);
  const latestTotalDurationRef = useRef(0);
  const lastSavedAtRef = useRef(0);

  // Stable refs for unmount save — avoids stale closure capture
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const lessonIdRef = useRef(lessonId);
  lessonIdRef.current = lessonId;
  const draftIdRef = useRef(draftId);
  draftIdRef.current = draftId;
  const isLessonContextRef = useRef(isLessonContext);
  isLessonContextRef.current = isLessonContext;

  // Load stored position on mount.
  // localStorage is read first (synchronous — survives hard refresh even when
  // the async Firestore write didn't complete before the page was torn down).
  // Firestore is then fetched async and wins if it holds a larger position
  // (e.g. the user watched further on another device).
  useEffect(() => {
    if (!isLessonContext || !userId) return;
    const localVt = getLocalPlaybackProgress(userId, draftId);
    if (localVt !== null) setSavedVirtualTime(localVt);
    getPlaybackProgress(userId, draftId).then((vt) => {
      if (vt !== null) setSavedVirtualTime((prev) => Math.max(prev, vt));
    }).catch(() => {});
  }, [isLessonContext, userId, draftId]);

  // Save latest position on unmount
  useEffect(() => {
    return () => {
      if (!isLessonContextRef.current) return;
      const vt = latestVirtualTimeRef.current;
      if (vt <= 0) return;
      savePlaybackProgress(
        userIdRef.current!,
        draftIdRef.current,
        lessonIdRef.current,
        vt,
        latestTotalDurationRef.current,
      ).catch(() => {});
    };
  }, []); // intentionally empty — runs once on unmount via refs

  // Called by LessonPreview on every video timeupdate (and on scrub).
  // localStorage is always written synchronously (cheap, no throttle needed).
  // Firestore writes are throttled to SAVE_INTERVAL_MS.
  const handleVirtualTimeChange = useCallback((virtualTime: number, totalDuration: number) => {
    latestVirtualTimeRef.current = virtualTime;
    latestTotalDurationRef.current = totalDuration;
    if (!isLessonContext || !userId) return;
    saveLocalPlaybackProgress(userId, draftId, virtualTime, totalDuration);
    const now = Date.now();
    if (now - lastSavedAtRef.current < SAVE_INTERVAL_MS) return;
    lastSavedAtRef.current = now;
    savePlaybackProgress(userId, draftId, lessonId, virtualTime, totalDuration).catch(() => {});
  }, [isLessonContext, userId, draftId, lessonId]);

  // Called by LessonPreview when the video pauses — saves immediately so that
  // a hard refresh right after pausing still restores the correct position.
  const handlePaused = useCallback((virtualTime: number, totalDuration: number) => {
    latestVirtualTimeRef.current = virtualTime;
    latestTotalDurationRef.current = totalDuration;
    if (!isLessonContext || !userId) return;
    lastSavedAtRef.current = Date.now();
    savePlaybackProgress(userId, draftId, lessonId, virtualTime, totalDuration).catch(() => {});
  }, [isLessonContext, userId, draftId, lessonId]);

  // Save on visibilitychange (tab switch, navigate away) and beforeunload
  // (hard refresh, closing the tab). Both handlers write to localStorage
  // synchronously — the Firestore write is also attempted but may not complete.
  useEffect(() => {
    if (!isLessonContext) return;
    const flush = () => {
      const vt = latestVirtualTimeRef.current;
      const td = latestTotalDurationRef.current;
      const uid = userIdRef.current;
      const did = draftIdRef.current;
      const lid = lessonIdRef.current;
      if (vt > 0 && uid) {
        saveLocalPlaybackProgress(uid, did, vt, td);
        savePlaybackProgress(uid, did, lid, vt, td).catch(() => {});
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', flush);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', flush);
    };
  }, [isLessonContext]);

  // On playback completion, clear the stored position so the next visit
  // starts from the beginning.
  const handleEnded = useCallback(() => {
    onEnded?.();
    if (!isLessonContext || !userId) return;
    latestVirtualTimeRef.current = 0;
    clearPlaybackProgress(userId, draftId).catch(() => {});
  }, [isLessonContext, userId, draftId, onEnded]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const draft = await getDraft(context, originalVideoId, draftId);
        if (!draft || cancelled) return;

        const loadedClips: VoiceoverNotableSegmentClip[] = draft.voiceoverNotableSegmentClips.map(
          (c) => ({ ...c })
        );
        const urlEntries = await Promise.all(
          loadedClips
            .filter((c) => c.videoStoragePath)
            .map(async (c) => {
              try {
                const url = await getStorageUrl(c.videoStoragePath!);
                return [c.id, url] as [string, string];
              } catch (err) {
                console.error('[LessonEditPreview] Storage URL resolution failed for clip', c.id, c.videoStoragePath, err);
                return null;
              }
            })
        );
        if (cancelled) return;

        const resolvedUrls = new Map<string, string>();
        urlEntries.forEach((e) => { if (e) resolvedUrls.set(e[0], e[1]); });

        setClips(loadedClips);
        setSegments(draft.notableSegments ?? []);
        setClipUrls(resolvedUrls);
      } catch (err) {
        if (!cancelled) setLoadError('Failed to load lesson preview.');
        console.error('LessonEditPreview: load error', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [context, originalVideoId, draftId]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="flex flex-col items-center gap-3 text-db-text-3">
          <div className="w-6 h-6 border-2 border-db-border border-t-biscayne rounded-full animate-spin" />
          <span className="text-sm">Loading lesson preview…</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <p className="text-red-500 text-sm">{loadError}</p>
      </div>
    );
  }

  const preview = (
    <LessonPreview
      clips={clips}
      notableSegments={segments}
      preloadedClipUrls={clipUrls}
      notes={notes}
      coachName={coachName}
      offeringLabel={offeringLabel}
      deliveredAt={deliveredAt}
      autoPlay={autoPlay}
      showControls={showControls}
      onEnded={handleEnded}
      onPlayingChange={onPlayingChange}
      downloadUrl={downloadUrl}
      downloadStoragePath={downloadStoragePath}
      className={isFullscreen ? 'flex-1 min-h-0' : className}
      variant="native"
      isFullscreen={isFullscreen}
      onFullscreenToggle={handleFullscreenToggle}
      initialVirtualTime={savedVirtualTime}
      onVirtualTimeChange={handleVirtualTimeChange}
      onPaused={handlePaused}
    />
  );

  /**
   * One subtree, in both states.
   *
   * This used to return `preview` in place and `createPortal(<div>{preview}</div>)`
   * when expanded. React reconciles by position, so the root element type
   * changed on every toggle and the whole subtree was unmounted and remounted:
   * the <video> was destroyed, its src reassigned, and currentIndex /
   * currentTime became fresh useState initialisers. That is what restarted the
   * lesson from the beginning.
   *
   * Now the wrapper is always here and only its className changes, so React
   * sees the same element in the same position and keeps the DOM node.
   *
   * `display: contents` for the in-flow case rather than no wrapper at all:
   * the wrapper generates no box, so the normal view lays out exactly as it did
   * when LessonPreview was returned directly — same parent, same flow, same
   * scrolling. `className` still goes to LessonPreview, untouched.
   *
   * The portal is gone rather than made permanent. A permanent portal to
   * document.body would tear the player out of its parent's layout in the
   * normal view, and switching the portal's container between body and an
   * in-place host remounts the children just as surely as switching the root
   * element did. `position: fixed` needs no portal here: the chain from this
   * component to the document has no transform, filter, perspective or
   * contain, so the viewport is already the containing block.
   */
  return (
    <div
      ref={containerRef}
      className={isFullscreen ? 'fixed inset-0 z-50 bg-black flex flex-col' : 'contents'}
    >
      {preview}
    </div>
  );
}

export default LessonEditPreview;
